export function addLeadTime(start, amount, unit) {
  const result = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 12);
  if (unit === 'business_days') {
    for (let remaining = amount; remaining > 0;) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0 && result.getDay() !== 6) remaining -= 1;
    }
  } else {
    result.setDate(result.getDate() + amount * (unit === 'weeks' ? 7 : 1));
  }
  return result;
}

export function arrivalRange(start, rule) {
  if (!rule || !Number.isInteger(rule.min) || !Number.isInteger(rule.max) || rule.min < 0 || rule.max < rule.min || !['business_days', 'days', 'weeks'].includes(rule.unit)) return null;
  return { from: addLeadTime(start, rule.min, rule.unit), to: addLeadTime(start, rule.max, rule.unit) };
}

export function destinationRule(rules, variantId, country) {
  const profile = rules.profiles[rules.variants[String(variantId)]];
  if (!profile) return { status: 'unknown' };
  const zone = profile.find(item => item.countries.includes(country)) || profile.find(item => item.restOfWorld);
  if (!zone) return { status: 'unsupported' };
  return zone.window ? { status: 'known', ...zone.window, freeShipping: zone.freeShipping === true } : { status: 'unknown', freeShipping: zone.freeShipping === true };
}

export function formatArrival(range, locale, today = new Date()) {
  const crossesYear = range.from.getFullYear() !== range.to.getFullYear() || range.to.getFullYear() !== today.getFullYear();
  const formatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', ...(crossesYear ? { year: 'numeric' } : {}) });
  return formatter.formatRange(range.from, range.to);
}

if (typeof window !== 'undefined' && !customElements.get('vh-delivery-estimate')) {
  const preferenceKey = 'vh_delivery_destination_v1';
  let rulesPromise;
  let countryPromise;
  const savedCountry = () => {
    try { return localStorage.getItem(preferenceKey); } catch { return null; }
  };
  const rememberCountry = country => {
    try { localStorage.setItem(preferenceKey, country); } catch { return; }
  };
  const detectedCountry = root => {
    if (!countryPromise) countryPromise = fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`, { credentials: 'same-origin', signal: AbortSignal.timeout(4500) })
      .then(response => response.ok ? response.json() : null)
      .then(result => result?.detected_values?.country || null)
      .catch(() => null);
    return countryPromise;
  };

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const country = form.querySelector('[name="country_code"]')?.value;
    if (country) rememberCountry(country);
  }, true);

  document.addEventListener('click', event => {
    const localization = event.target.closest('localization-form');
    const country = localization?.querySelector('[name="country_code"]')?.value;
    if (country && event.target.closest('[data-value]')) rememberCountry(country);
  });

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.selector = this.querySelector('select');
      this.arrival = this.querySelector('[data-arrival]');
      this.arrivalLabel = this.querySelector('[data-arrival-label]');
      this.countryName = this.querySelector('[data-country-name]');
      this.countryFlag = this.querySelector('[data-country-flag]');
      this.destinationLabel = this.querySelector('[data-destination-label]');
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.onVariantChange = () => this.renderEstimate();
      document.addEventListener('variant:change', this.onVariantChange, options);
      document.addEventListener('change', event => {
        if (event.target instanceof HTMLInputElement && event.target.form?.id === this.dataset.formId) this.renderEstimate();
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.renderEstimate();
      }, options);
      this.selector.addEventListener('change', () => {
        this.manualChoice = true;
        this.resolvingCountry = false;
        rememberCountry(this.selector.value);
        this.renderEstimate();
      }, options);
      const saved = savedCountry();
      if (saved && this.hasCountry(saved)) {
        this.selector.value = saved;
        this.manualChoice = true;
      }
      this.countryName.textContent = this.selector.selectedOptions[0]?.textContent || this.selector.value;
      this.syncDestination();
      this.resolvingCountry = !this.manualChoice;
      rulesPromise ||= fetch(this.dataset.rulesUrl, { signal: AbortSignal.timeout(6000) }).then(response => {
        if (!response.ok) throw new Error('Delivery rules unavailable');
        return response.json();
      });
      rulesPromise.then(rules => {
        if (!this.isConnected) return;
        this.rules = rules;
        this.renderEstimate();
      }).catch(() => {
        if (this.isConnected) this.showMessage('unknown');
      });
      if (!this.manualChoice) detectedCountry(this.dataset.root).then(country => {
        if (!this.isConnected || this.manualChoice) return;
        if (country?.handle) {
          if (!this.hasCountry(country.handle)) {
            const option = document.createElement('option');
            option.value = country.handle;
            option.textContent = country.name || country.handle;
            this.selector.append(option);
          }
          this.selector.value = country.handle;
        }
        this.resolvingCountry = false;
        this.renderEstimate();
      });
      const now = new Date();
      this.scheduleRefresh(now);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearTimeout(this.midnightTimer);
    }

    hasCountry(country) {
      return Array.from(this.selector.options).some(option => option.value === country);
    }

    scheduleRefresh(now = new Date()) {
      const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1);
      this.midnightTimer = setTimeout(() => {
        this.renderEstimate();
        this.scheduleRefresh();
      }, nextDay - now);
    }

    showMessage(key) {
      this.arrival.textContent = this.copy[key];
      this.arrivalLabel.hidden = true;
      delete this.dataset.dateFrom;
      delete this.dataset.dateTo;
    }

    syncDestination(freeShipping = false) {
      const country = this.selector.value.toUpperCase();
      this.countryName.textContent = this.selector.selectedOptions[0]?.textContent || country;
      this.countryFlag.textContent = /^[A-Z]{2}$/.test(country) ? String.fromCodePoint(...Array.from(country, letter => 127397 + letter.charCodeAt(0))) : '';
      this.destinationLabel.textContent = freeShipping ? this.copy.free_to : this.copy.to;
      this.dataset.resolvedCountry = country;
      this.closest('[id^="ProductInfo-"]')?.querySelectorAll('[data-shipping-benefit]').forEach(badge => {
        badge.textContent = freeShipping ? badge.dataset.freeShippingLabel : badge.dataset.shippingLabel;
      });
    }

    renderEstimate() {
      this.syncDestination();
      if (!this.rules || this.resolvingCountry) return;
      const form = document.getElementById(this.dataset.formId);
      const picker = form?.closest('[id^="ProductInfo-"]')?.querySelector('variant-radios, variant-selects');
      if (picker && 'currentVariant' in picker && !picker.currentVariant) {
        this.showMessage('sold_out');
        return;
      }
      const variantId = form?.querySelector('[name="id"]')?.value;
      const rule = destinationRule(this.rules, variantId, this.selector.value);
      this.syncDestination(rule.freeShipping);
      const variant = this.variants.find(item => String(item.id) === String(variantId));
      if (!variant || !variant.available) {
        this.showMessage('sold_out');
        return;
      }
      const today = new Date();
      const range = rule.status === 'known' ? arrivalRange(today, rule) : null;
      if (!range) {
        this.showMessage(rule.status === 'unsupported' ? 'unsupported' : 'unknown');
        return;
      }
      this.arrival.textContent = formatArrival(range, this.dataset.locale, today);
      this.arrivalLabel.hidden = false;
      this.dataset.variantId = String(variantId);
      this.dataset.dateFrom = `${range.from.getFullYear()}-${String(range.from.getMonth() + 1).padStart(2, '0')}-${String(range.from.getDate()).padStart(2, '0')}`;
      this.dataset.dateTo = `${range.to.getFullYear()}-${String(range.to.getMonth() + 1).padStart(2, '0')}-${String(range.to.getDate()).padStart(2, '0')}`;
    }
  }
  customElements.define('vh-delivery-estimate', DeliveryEstimate);
}
